import React, { useEffect, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { useSettings } from '@/context/SettingsContext';

/**
 * Renders the order receipt as printable HTML inside a hidden iframe, then
 * triggers `window.print()` on the iframe. The browser's print dialog has a
 * native "Save as PDF" option, so this covers both physical printing and PDF.
 */
const buildReceiptHTML = ({ order, outlet, settings }) => {
  const sym = settings?.general?.currency_symbol || '$';
  const r = settings?.receipt || {};
  const items = order.items || [];
  const fmt = (n) => `${sym}${(Number(n) || 0).toFixed(2)}`;
  const dt = order.created_at ? new Date(order.created_at) : new Date();
  const dateStr = dt.toLocaleString();
  const fontPx = r.font_size === 'small' ? 11 : r.font_size === 'large' ? 14 : 12;
  const widthMm = r.paper_size === '58mm' ? 58 : r.paper_size === 'A4' ? 210 : 80;
  const baseUrl = (outlet?.public_base_url || '').replace(/\/$/, '') || window.location.origin;
  const orderQRUrl = r.show_order_qr
    ? `${baseUrl}/order-status/${order.id}`
    : null;

  const itemRows = items.map((it) => {
    const name = it.name || it.menu_item_name || 'Item';
    const qty = it.quantity || 1;
    const price = (it.price ?? it.base_price ?? 0) + (it.modifier_total || 0);
    const line = it.item_total ?? price * qty;
    const mods = (it.modifiers || []).map((m) => m.option_name || m.name).filter(Boolean).join(', ');
    return `
      <tr>
        <td>${qty}× ${name}${mods ? `<br><small style="opacity:.7">${mods}</small>` : ''}</td>
        <td style="text-align:right;white-space:nowrap;">${fmt(line)}</td>
      </tr>`;
  }).join('');

  const orderType = (order.order_type || 'dine_in').replace(/_/g, ' ').toUpperCase();

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Receipt #${order.order_number || order.id}</title>
  <style>
    @page { size: ${widthMm}mm auto; margin: 4mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Courier New', monospace; font-size: ${fontPx}px; color:#000; margin:0; padding:8px; width:${widthMm}mm; }
    h1 { font-size: ${fontPx + 4}px; margin: 4px 0; text-align:center; }
    h2 { font-size: ${fontPx + 1}px; margin: 4px 0; text-align:center; }
    table { width:100%; border-collapse: collapse; }
    td { padding: 2px 0; vertical-align: top; }
    .divider { border-top: 1px dashed #000; margin: 6px 0; }
    .row { display:flex; justify-content:space-between; padding:1px 0; }
    .center { text-align:center; }
    .right { text-align:right; }
    .bold { font-weight:700; }
    .muted { opacity:.75; font-size: ${fontPx - 1}px; }
    .total { font-size: ${fontPx + 2}px; font-weight:700; border-top:1px solid #000; border-bottom:1px solid #000; padding:4px 0; }
    .qr { margin-top:8px; text-align:center; }
    .qr img { width: 80px; height:80px; }
    @media print {
      body { padding:0; }
    }
  </style>
</head>
<body>
  ${r.show_logo && outlet?.logo_url ? `<div class="center"><img src="${outlet.logo_url}" alt="logo" style="max-width:60mm;max-height:18mm" /></div>` : ''}
  ${r.header_text ? `<h2>${r.header_text}</h2>` : ''}
  <h1>${outlet?.name || 'Receipt'}</h1>
  ${r.show_store_address && outlet?.address ? `<div class="center muted">${outlet.address}</div>` : ''}
  ${r.show_store_phone && outlet?.phone ? `<div class="center muted">${outlet.phone}</div>` : ''}
  <div class="divider"></div>
  <div class="row"><span>Order #</span><span class="bold">${order.order_number || ''}</span></div>
  <div class="row"><span>Type</span><span>${orderType}</span></div>
  ${r.show_cashier_name && order.cashier_name ? `<div class="row"><span>Cashier</span><span>${order.cashier_name}</span></div>` : ''}
  ${order.customer_name ? `<div class="row"><span>Customer</span><span>${order.customer_name}</span></div>` : ''}
  ${order.delivery_address ? `<div class="muted">Delivery: ${order.delivery_address}</div>` : ''}
  ${order.delivery_distance_miles ? `<div class="muted">Distance: ${order.delivery_distance_miles} mi · ETA ~${order.delivery_eta_minutes || ''} min</div>` : ''}
  ${order.promised_time ? `<div class="muted">Promised: ${new Date(order.promised_time).toLocaleString()}</div>` : ''}
  <div class="row"><span>Date</span><span>${dateStr}</span></div>
  <div class="divider"></div>
  <table>${itemRows}</table>
  <div class="divider"></div>
  <div class="row"><span>Subtotal</span><span>${fmt(order.subtotal)}</span></div>
  ${order.service_charge ? `<div class="row"><span>Service</span><span>${fmt(order.service_charge)}</span></div>` : ''}
  ${r.show_tax_breakdown && order.tax ? `<div class="row"><span>Tax</span><span>${fmt(order.tax)}</span></div>` : ''}
  ${order.delivery_fee ? `<div class="row"><span>Delivery</span><span>${fmt(order.delivery_fee)}</span></div>` : ''}
  <div class="row total"><span>TOTAL</span><span>${fmt(order.total)}</span></div>
  ${order.payment_method ? `<div class="row muted"><span>Paid (${order.payment_method})</span><span>${fmt(order.total)}</span></div>` : ''}
  ${orderQRUrl ? `
    <div class="qr">
      <img src="https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(orderQRUrl)}" alt="order qr"/>
      <div class="muted">Scan for live status</div>
    </div>` : ''
  }
  ${r.footer_text ? `<div class="divider"></div><div class="center muted">${r.footer_text}</div>` : ''}
</body>
</html>`;
};

export const printReceipt = ({ order, outlet, settings }) => {
  if (!order) return;
  // Build an off-screen iframe so we don't disrupt the main page
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  const html = buildReceiptHTML({ order, outlet, settings });
  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Wait for images (logo, QR) before printing
  const trigger = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (e) {
      console.error('Print failed:', e);
    }
    // Clean up after a moment (Chrome needs the iframe to stay until print dialog closes)
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };
  // Give the QR/logo a chance to load
  setTimeout(trigger, 350);
};

// Hidden component just to make print available declaratively in JSX
export const ReceiptPrinter = ({ orderToPrint, onDone }) => {
  const { currentOutlet } = useApp();
  const { settings } = useSettings();
  const fired = useRef(false);

  useEffect(() => {
    if (!orderToPrint || fired.current) return;
    fired.current = true;
    printReceipt({ order: orderToPrint, outlet: currentOutlet, settings });
    if (onDone) onDone();
  }, [orderToPrint, currentOutlet, settings, onDone]);

  return null;
};

export default ReceiptPrinter;
